import { useState } from 'react';
import { Save, RefreshCw, ExternalLink, GitCommit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface BotConfigurationProps {
  bot: {
    id: string;
    name: string;
    version?: string;
    status: string;
    environment?: string;
    description?: string;
    repository?: string;
    lastDeploy?: string;
    uptime: number;
    memory: number;
    cpu: number;
    requests: number;
  };
}

// Mock configuration data
const mockConfig = {
  basic: {
    name: 'Lead Qualification Bot',
    description: 'Automated lead qualification and initial contact bot',
    timeout: 30000,
    maxRetries: 3,
    enabled: true
  },
  ai: {
    model: 'gpt-4-turbo',
    temperature: 0.7,
    maxTokens: 1000,
    systemPrompt: 'You are a helpful lead qualification assistant...'
  },
  voice: {
    provider: 'vapi',
    voice: 'nova',
    speed: 1.0,
    stability: 0.5
  },
  webhooks: {
    onCallStart: 'https://api.company.com/webhooks/call-start',
    onCallEnd: 'https://api.company.com/webhooks/call-end',
    onError: 'https://api.company.com/webhooks/error'
  }
};

export function BotConfiguration({ bot }: BotConfigurationProps) {
  const [config, setConfig] = useState(mockConfig);
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    setIsModified(false);
    console.log('Configuration saved:', config);
  };

  const handleReset = () => {
    setConfig(mockConfig);
    setIsModified(false);
  };

  const updateConfig = (section: string, field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
    setIsModified(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Bot Configuration</h3>
          <p className="text-sm text-muted-foreground">
            Manage bot settings and behavior
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {isModified && (
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleReset}
            disabled={!isModified}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!isModified || isSaving}
          >
            <Save className="w-3 h-3 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Bot Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bot Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-xs text-muted-foreground">Environment</Label>
              <div className="font-medium">{bot.environment}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Version</Label>
              <div className="font-mono">{bot.version}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Last Deploy</Label>
              <div className="text-sm">{formatDate(bot.lastDeploy)}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Repository</Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-auto p-0 text-sm text-blue-600"
                onClick={() => window.open('https://github.com/company/bot', '_blank')}
              >
                View on GitHub
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basic Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="name">Bot Name</Label>
              <Input
                id="name"
                value={config.basic.name}
                onChange={(e) => updateConfig('basic', 'name', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={config.basic.description}
                onChange={(e) => updateConfig('basic', 'description', e.target.value)}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="timeout">Timeout (ms)</Label>
                <Input
                  id="timeout"
                  type="number"
                  value={config.basic.timeout}
                  onChange={(e) => updateConfig('basic', 'timeout', parseInt(e.target.value))}
                />
              </div>
              
              <div>
                <Label htmlFor="retries">Max Retries</Label>
                <Input
                  id="retries"
                  type="number"
                  value={config.basic.maxRetries}
                  onChange={(e) => updateConfig('basic', 'maxRetries', parseInt(e.target.value))}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={config.basic.enabled}
                onCheckedChange={(checked) => updateConfig('basic', 'enabled', checked)}
              />
              <Label htmlFor="enabled">Bot Enabled</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">AI Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                value={config.ai.model}
                onChange={(e) => updateConfig('ai', 'model', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="temperature">Temperature</Label>
              <Input
                id="temperature"
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={config.ai.temperature}
                onChange={(e) => updateConfig('ai', 'temperature', parseFloat(e.target.value))}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="maxTokens">Max Tokens</Label>
            <Input
              id="maxTokens"
              type="number"
              value={config.ai.maxTokens}
              onChange={(e) => updateConfig('ai', 'maxTokens', parseInt(e.target.value))}
            />
          </div>
          
          <div>
            <Label htmlFor="systemPrompt">System Prompt</Label>
            <Textarea
              id="systemPrompt"
              value={config.ai.systemPrompt}
              onChange={(e) => updateConfig('ai', 'systemPrompt', e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Voice Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Voice Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="provider">Provider</Label>
              <Input
                id="provider"
                value={config.voice.provider}
                onChange={(e) => updateConfig('voice', 'provider', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="voice">Voice</Label>
              <Input
                id="voice"
                value={config.voice.voice}
                onChange={(e) => updateConfig('voice', 'voice', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="speed">Speed</Label>
              <Input
                id="speed"
                type="number"
                step="0.1"
                min="0.5"
                max="2.0"
                value={config.voice.speed}
                onChange={(e) => updateConfig('voice', 'speed', parseFloat(e.target.value))}
              />
            </div>
            
            <div>
              <Label htmlFor="stability">Stability</Label>
              <Input
                id="stability"
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={config.voice.stability}
                onChange={(e) => updateConfig('voice', 'stability', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Webhooks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="callStart">On Call Start</Label>
            <Input
              id="callStart"
              value={config.webhooks.onCallStart}
              onChange={(e) => updateConfig('webhooks', 'onCallStart', e.target.value)}
              placeholder="https://api.company.com/webhooks/call-start"
            />
          </div>
          
          <div>
            <Label htmlFor="callEnd">On Call End</Label>
            <Input
              id="callEnd"
              value={config.webhooks.onCallEnd}
              onChange={(e) => updateConfig('webhooks', 'onCallEnd', e.target.value)}
              placeholder="https://api.company.com/webhooks/call-end"
            />
          </div>
          
          <div>
            <Label htmlFor="error">On Error</Label>
            <Input
              id="error"
              value={config.webhooks.onError}
              onChange={(e) => updateConfig('webhooks', 'onError', e.target.value)}
              placeholder="https://api.company.com/webhooks/error"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}